// src/components/leads/LeadsPage.jsx

import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Table,
  Button,
  Form,
  InputGroup,
  Dropdown,
  DropdownButton,
} from "react-bootstrap";
import Header from "../header/header";               // Adjust path if needed
import styles from "./leads.module.scss";            // SCSS for styling
import AddTeamForm from "./add-team";                // We build this next
import EditTeamForm from "./edit-team";              // We build this next
import { db } from "../../firebase";                 // Firestore export
import { CustomSpinner } from "../shared/Spinner";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ALL_SUBSCRIPTION_TYPES,
  SUBSCRIPTION_TYPE_OPTIONS,
  isExcludedSubscriptionType,
} from "./subscription-types";
import {
  calendarDateSortValue,
  formatCalendarDate,
} from "../../utils/calendarDate";

export const LeadsPage = () => {
  // ─── State Hooks ─────────────────────────────────────────────────────
  const [teams, setTeams] = useState([]);       // raw data from Firestore
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Search + Filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("All Statuses");
  const [filterConference, setFilterConference] = useState("All Conferences");
  const [filterSubscriptionType, setFilterSubscriptionType] = useState(
    ALL_SUBSCRIPTION_TYPES
  );

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Add / Edit form toggles
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  
  // New modal states
  const [showTeamNotesModal, setShowTeamNotesModal] = useState(false);
  const [showContactManagementModal, setShowContactManagementModal] = useState(false);

  // ─── Fetch Teams from Firestore ────────────────────────────────────────
  const getTeams = useCallback(async () => {
    try {
      setLoading(true);
      const snapshot = await db.collection("Teams").get();
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setTeams(data);
    } catch (err) {
      console.error("Error fetching teams:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getTeams();
  }, [getTeams]);

  // ─── Search + Filter Handlers ─────────────────────────────────────────
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  const handleStatusFilter = (status) => {
    setFilterStatus(status);
  };
  const handleConferenceFilter = (conf) => {
    setFilterConference(conf);
  };
  const handleSubscriptionTypeFilter = (value) => {
    setFilterSubscriptionType(value);
  };

  // ─── Sorting Helpers ─────────────────────────────────────────────────
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };
  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "asc" ? "▲" : "▼";
    }
    return null;
  };

  // ─── Apply Search + Filters ───────────────────────────────────────────
  const filteredTeams = useMemo(() => {
    return teams.filter((team) => {
      // 1) Search by teamName only (since contacts are in subcollection)
      const matchesSearch = team.teamName
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      // 2) Filter by status
      const matchesStatus =
        filterStatus === "All Statuses" || team.status === filterStatus;

      // 3) Filter by conference
      const matchesConference =
        filterConference === "All Conferences" ||
        team.conference === filterConference;

      // 4) Filter by subscription type (stored as division)
      const matchesSubscriptionType =
        filterSubscriptionType === ALL_SUBSCRIPTION_TYPES ||
        team.division === filterSubscriptionType;

      return (
        matchesSearch &&
        matchesStatus &&
        matchesConference &&
        matchesSubscriptionType
      );
    });
  }, [
    teams,
    searchTerm,
    filterStatus,
    filterConference,
    filterSubscriptionType,
  ]);

  const subscriptionFilterChoices = useMemo(() => {
    const legacy = Array.from(
      new Set(teams.map((t) => t.division).filter(Boolean))
    ).filter(
      (d) =>
        !SUBSCRIPTION_TYPE_OPTIONS.includes(d) && !isExcludedSubscriptionType(d)
    );
    return [...SUBSCRIPTION_TYPE_OPTIONS, ...legacy.sort()];
  }, [teams]);

  // ─── Sort the filtered list ────────────────────────────────────────────
  const sortedTeams = useMemo(() => {
    if (!sortConfig.key) return filteredTeams;

    return [...filteredTeams].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // If sorting by a date field (e.g. lastContact), use calendar-day values
      if (["lastContact"].includes(sortConfig.key)) {
        aVal = calendarDateSortValue(aVal);
        bVal = calendarDateSortValue(bVal);
      } else {
        // string compare
        if (typeof aVal === "string") aVal = aVal.toLowerCase();
        if (typeof bVal === "string") bVal = bVal.toLowerCase();
      }

      // push null/undefined to bottom
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredTeams, sortConfig]);

  // ─── Row Actions ──────────────────────────────────────────────────────
  // 1) Open Edit modal (pass entire team object)
  const handleEditTeam = (team) => {
    setSelectedTeam(team);
    setShowEditForm(true);
  };

  // 2) Delete a team (and all its subcollections will be orphaned unless you cascade‐delete)
  //    You might choose to add a Cloud Function to cascade‐delete subcollections if needed.
  const handleDeleteTeam = async (id, teamName) => {
    if (!window.confirm(`Delete team "${teamName}"? This cannot be undone.`)) {
      return;
    }
    try {
      await db.collection("Teams").doc(id).delete();
      toast.success("Team deleted successfully");
      getTeams();
    } catch (err) {
      console.error("Error deleting team:", err);
      toast.error("Error deleting team");
    }
  };

  // 3) Open Team Notes modal
  const handleTeamNotes = (team) => {
    setSelectedTeam(team);
    setShowTeamNotesModal(true);
  };

  // 4) Open Contact Management modal
  const handleContactManagement = (team) => {
    setSelectedTeam(team);
    setShowContactManagementModal(true);
  };

  // ─── Render Each Row ──────────────────────────────────────────────────
  // We show “# of contacts” in the Contact column.
  // When editing, EditTeamForm will fetch actual contacts by teamId.
  const renderTeams = useMemo(
    () =>
      sortedTeams.map((team) => {
        const {
          id,
          teamName,
          conference,
          division,
          status,
          assignedStaff,
          lastContact,
        } = team;

        // We'll show the count of contacts by reading `team.contactsCount` if you store it,
        // or just show “View/Edit” for contact management in EditTeamForm.
        // For now, we’ll query Firestore directly when rendering rows to get contactCount.
        // BUT because that would be another async call, we'll keep it simple: display a placeholder
        // “Manage Contacts” button in the table. The actual contacts live in the edit form.

        return (
          <tr key={id}>
            {/* Team Name */}
            <td style={{ whiteSpace: "nowrap" }}>{teamName || "-"}</td>

            {/* Conference */}
            <td style={{ whiteSpace: "nowrap" }}>{conference || "-"}</td>

            {/* Status */}
            <td>{status || "-"}</td>

            {/* Contact column: show a “Manage” link (actual contacts are edited in the modal) */}
            <td style={{ whiteSpace: "nowrap" }}>
              <Button
                variant="outline-info"
                size="sm"
                onClick={() => handleContactManagement(team)}
              >
                Manage Contacts
              </Button>
            </td>

            {/* Assigned Staff */}
            <td>{assignedStaff || "-"}</td>

            {/* Last Contact */}
            <td>
              {formatCalendarDate(lastContact)}
            </td>

            {/* Subscription type (division) */}
            <td style={{ whiteSpace: "nowrap" }}>{division || "-"}</td>

            {/* Actions */}
            <td>
              <div className="d-flex gap-1">
                {/* Edit Team Info */}
                <Button
                  variant="outline-success"
                  size="sm"
                  onClick={() => handleEditTeam(team)}
                >
                  Edit
                </Button>

                {/* Team Notes */}
                <Button
                  variant="outline-warning"
                  size="sm"
                  onClick={() => handleTeamNotes(team)}
                >
                  Notes
                </Button>

                {/* Delete Team */}
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleDeleteTeam(id, teamName)}
                >
                  Delete
                </Button>
              </div>
            </td>
          </tr>
        );
      }),
    [sortedTeams]
  );

  // ─── Early Returns ────────────────────────────────────────────────────
  if (loading) return <CustomSpinner />;
  if (error) return <div>Error loading team data: {error.message}</div>;

  // ─── JSX Output ───────────────────────────────────────────────────────
  return (
    <>
      <Header />

      <div className={styles.container}>
        {/* ─── Title + “Add Team” Button ────────────────────────────── */}
        <div className={styles.titleRow}>
          <div>
            <h4>Teams / Leads</h4>
            <small>
              This section is used to track dialogue with all current and
              prospective clients, across all subscription types
            </small>
          </div>
          <Button onClick={() => setShowAddForm(true)}>+ Add Team</Button>
        </div>

        {/* ─── Search + Filter Controls ─────────────────────────────── */}
        <div className={styles.filterRow}>
          <InputGroup style={{ maxWidth: 350 }}>
            <Form.Control
              placeholder="Search teams..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </InputGroup>

          {/* Status Filter */}
          <DropdownButton
            title={filterStatus}
            variant="outline-secondary"
            id="dropdown-status"
            style={{ marginLeft: "1rem" }}
          >
            <Dropdown.Item onClick={() => handleStatusFilter("All Statuses")}>
              All Statuses
            </Dropdown.Item>
            <Dropdown.Item onClick={() => handleStatusFilter("Active")}>
              Active
            </Dropdown.Item>
            <Dropdown.Item onClick={() => handleStatusFilter("Pending")}>
              Pending
            </Dropdown.Item>
            <Dropdown.Item onClick={() => handleStatusFilter("Inactive")}>
              Inactive
            </Dropdown.Item>
          </DropdownButton>

          {/* Subscription type filter */}
          <DropdownButton
            title={filterSubscriptionType}
            variant="outline-secondary"
            id="dropdown-subscription-type"
            style={{ marginLeft: "0.5rem" }}
          >
            <Dropdown.Item
              onClick={() =>
                handleSubscriptionTypeFilter(ALL_SUBSCRIPTION_TYPES)
              }
            >
              {ALL_SUBSCRIPTION_TYPES}
            </Dropdown.Item>
            {subscriptionFilterChoices.map((opt) => (
              <Dropdown.Item
                key={opt}
                onClick={() => handleSubscriptionTypeFilter(opt)}
              >
                {opt}
              </Dropdown.Item>
            ))}
          </DropdownButton>

          {/* Conference Filter */}
          <DropdownButton
            title={filterConference}
            variant="outline-secondary"
            id="dropdown-conference"
            style={{ marginLeft: "0.5rem" }}
          >
            <Dropdown.Item onClick={() => handleConferenceFilter("All Conferences")}>
              All Conferences
            </Dropdown.Item>
            {Array.from(new Set(teams.map((t) => t.conference)))
              .filter((c) => c)
              .map((conf) => (
                <Dropdown.Item
                  key={conf}
                  onClick={() => handleConferenceFilter(conf)}
                >
                  {conf}
                </Dropdown.Item>
              ))}
          </DropdownButton>
        </div>

        {/* ─── Table of Teams ────────────────────────────────────────── */}
        <div className={styles.contentBody}>
          <Table responsive hover>
            <thead>
              <tr>
                <th
                  style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                  onClick={() => requestSort("teamName")}
                >
                  Team Name {getSortIndicator("teamName")}
                </th>
                <th
                  style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                  onClick={() => requestSort("conference")}
                >
                  Conference {getSortIndicator("conference")}
                </th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => requestSort("status")}
                >
                  Status {getSortIndicator("status")}
                </th>
                <th>Contacts</th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => requestSort("assignedStaff")}
                >
                  Assigned Staff {getSortIndicator("assignedStaff")}
                </th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => requestSort("lastContact")}
                >
                  Last Contact {getSortIndicator("lastContact")}
                </th>
                <th
                  style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                  onClick={() => requestSort("division")}
                >
                  Subscription Type {getSortIndicator("division")}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>{renderTeams}</tbody>
          </Table>
        </div>
      </div>

      {/* ─── Add / Edit Modals ─────────────────────────────────────── */}
      <AddTeamForm
        show={showAddForm}
        handleClose={() => setShowAddForm(false)}
        refreshTeams={getTeams}
      />

      {selectedTeam && (
        <EditTeamForm
          show={showEditForm}
          handleClose={() => setShowEditForm(false)}
          team={selectedTeam}
          refreshTeams={getTeams}
        />
      )}

      {/* Team Notes Modal */}
      {selectedTeam && (
        <EditTeamForm
          show={showTeamNotesModal}
          handleClose={() => setShowTeamNotesModal(false)}
          team={selectedTeam}
          refreshTeams={getTeams}
          mode="teamNotes"
        />
      )}

      {/* Contact Management Modal */}
      {selectedTeam && (
        <EditTeamForm
          show={showContactManagementModal}
          handleClose={() => setShowContactManagementModal(false)}
          team={selectedTeam}
          refreshTeams={getTeams}
          mode="contactManagement"
        />
      )}
    </>
  );
};
